import { getIsPatentSource } from '#pplx/frontend/common/files/sourceClassification.js';
import { isDownloadableUrl } from '#pplx/frontend/common/files/urls.js';
import { defineMessages } from 'react-intl';
const LocalMCPServerName = {
    APPLE_HEALTHKIT_LOCAL_MCP: 'APPLE_HEALTHKIT_LOCAL_MCP',
    LOCAL_MCP: 'LOCAL_MCP',
};
// in sync with backend AttachmentProcessingErrorCodes
export var AttachmentProcessingErrorCodes;
(function (AttachmentProcessingErrorCodes) {
    AttachmentProcessingErrorCodes["token_limit_exceeded"] = "token_limit_exceeded";
    AttachmentProcessingErrorCodes["parsing_error"] = "parsing_error";
    AttachmentProcessingErrorCodes["audio_duration_limit"] = "audio_duration_limit";
    AttachmentProcessingErrorCodes["audio_empty_content"] = "audio_empty_content";
    AttachmentProcessingErrorCodes["unknown_error"] = "unknown_error";
    AttachmentProcessingErrorCodes["unauthorized"] = "unauthorized";
    AttachmentProcessingErrorCodes["no_data_received"] = "no_data_received";
    AttachmentProcessingErrorCodes["file_type_not_supported"] = "file_type_not_supported";
    AttachmentProcessingErrorCodes["image_failed_moderation"] = "image_failed_moderation";
    AttachmentProcessingErrorCodes["failed_security_check"] = "failed_security_check";
})(AttachmentProcessingErrorCodes || (AttachmentProcessingErrorCodes = {}));
export var AttachmentUploadErrorCodes;
(function (AttachmentUploadErrorCodes) {
    AttachmentUploadErrorCodes["generic_upload_error"] = "generic_upload_error";
    AttachmentUploadErrorCodes["unsupported_type"] = "unsupported_type";
    AttachmentUploadErrorCodes["too_large"] = "too_large";
    AttachmentUploadErrorCodes["too_small"] = "too_small";
    AttachmentUploadErrorCodes["no_name"] = "no_name";
    AttachmentUploadErrorCodes["failed_moderation"] = "failed_moderation";
    AttachmentUploadErrorCodes["over_file_count"] = "over_file_count";
    AttachmentUploadErrorCodes["over_image_count"] = "over_image_count";
    AttachmentUploadErrorCodes["attachments_disabled_by_organization"] = "attachments_disabled_by_organization";
    AttachmentUploadErrorCodes["over_batch_size"] = "over_batch_size";
    AttachmentUploadErrorCodes["over_file_size"] = "over_file_size";
})(AttachmentUploadErrorCodes || (AttachmentUploadErrorCodes = {}));
// When skipParsing is true (e.g. Computer/ASI mode), certain processing errors are
// ignorable because the agent only needs the raw file URL, not parsed content.
// no_data_received occurs when the processing worker times out — the file upload
// succeeded but text extraction didn't complete, which is irrelevant for skipParsing.
// image_failed_moderation is deliberately excluded — those must always surface.
export function isIgnorableAttachmentError({ skipParsing, errorCode, }) {
    if (!skipParsing || errorCode === null)
        return false;
    return (errorCode === AttachmentProcessingErrorCodes.parsing_error ||
        errorCode === AttachmentProcessingErrorCodes.audio_duration_limit ||
        errorCode === AttachmentProcessingErrorCodes.audio_empty_content ||
        errorCode === AttachmentProcessingErrorCodes.file_type_not_supported ||
        errorCode === AttachmentProcessingErrorCodes.no_data_received);
}
export function isInlineAttachmentResult(webResult) {
    const repoType = webResult.file_metadata?.file_repository_type;
    const metaData = webResult.meta_data;
    return !!webResult.is_attachment && !repoType && !metaData?.file_uuid;
}
export const ATTACHMENT_FAILURE_MESSAGES = defineMessages({
    token_limit_exceeded: {
        id: '0f38e1e1af',
        description: 'Token_limit_exceeded message in the attachments Utils shared utilities section',
        defaultMessage: "File {filename} exceeds the model's token limit.",
    },
    audio_empty_content: {
        id: '43c4faf4fc',
        defaultMessage: 'The attached audio file contains no detectable speech.',
        description: 'Attachment error when audio transcription returns no text',
    },
    audio_duration_limit: {
        defaultMessage: 'Audio exceeds the 60-minute limit.',
        id: 'af265dd558',
        description: 'Attachment error when audio is longer than the transcription limit',
    },
    parsing_error: {
        id: '4cb20b85fe',
        description: 'Parsing_error message in the attachments Utils shared utilities section',
        defaultMessage: 'Failed to parse file {filename}.',
    },
    file_type_not_supported: {
        id: '72a527a920',
        description: 'File_type_not_supported message in the attachments Utils shared utilities section',
        defaultMessage: 'File type not supported for {filename}.',
    },
    unknown_error: {
        id: '8cb90e2f90',
        description: 'Unknown_error message in the attachments Utils shared utilities section',
        defaultMessage: 'Unknown error for file {filename}.',
    },
    unauthorized: {
        id: '50c882e291',
        description: 'Unauthorized message in the attachments Utils shared utilities section',
        defaultMessage: "You don't have access to {filename}.",
    },
    no_data_received: {
        id: '46da6143ad',
        description: 'No_data_received message in the attachments Utils shared utilities section',
        defaultMessage: 'No data received for file {filename}.',
    },
    generic_upload_error: {
        id: 'c66c406dfb',
        description: 'Generic_upload_error message in the attachments Utils shared utilities section',
        defaultMessage: 'File upload failed',
    },
    unsupported_type: {
        id: '345ead7ec2',
        description: 'Unsupported_type message in the attachments Utils shared utilities section',
        defaultMessage: 'The file type you uploaded is not supported.',
    },
    too_large: {
        id: '59b912934b',
        description: 'Too_large message in the attachments Utils shared utilities section',
        defaultMessage: 'File too large: {max} maximum.',
    },
    too_small: {
        id: 'f68764e722',
        description: 'Too_small message in the attachments Utils shared utilities section',
        defaultMessage: 'File too small.',
    },
    no_name: {
        id: 'f3b90b7506',
        description: 'No_name message in the attachments Utils shared utilities section',
        defaultMessage: 'File name is required.',
    },
    image_failed_moderation: {
        id: 'ee32dffd3b',
        description: 'Image_failed_moderation message in the attachments Utils shared utilities section',
        defaultMessage: 'File {filename} failed moderation.',
    }, // for AWS Rekognition status on S3 images
    failed_security_check: {
        id: '1675595167',
        description: 'Failed_security_check message in the attachments Utils shared utilities section',
        defaultMessage: 'File {filename} failed security validation.',
    },
    failed_moderation: {
        id: 'bfe285a46c',
        description: 'Failed_moderation message in the attachments Utils shared utilities section',
        defaultMessage: 'File upload failed moderation.',
    }, // for cloudinary status
    over_file_count: {
        id: '53780539dc',
        description: 'Over_file_count message in the attachments Utils shared utilities section',
        defaultMessage: 'You are allowed up to {maxNumFiles, plural, one {# file} other {# files}} at once.',
    },
    over_image_count: {
        id: '2fee532e26',
        description: 'Over_image_count message in the attachments Utils shared utilities section',
        defaultMessage: 'You are allowed up to {maxNumImages, plural, one {# image} other {# images}} at once.',
    },
    attachments_disabled_by_organization: {
        id: '9cc402a3ac',
        description: 'Attachments_disabled_by_organization message in the attachments Utils shared utilities section',
        defaultMessage: 'Your organization has disabled attachments.',
    },
    over_batch_size: {
        id: 'e819d93335',
        description: 'Error shown when the files attached to one message exceed the total size limit for the batch',
        defaultMessage: 'Attachments exceed the total size limit for on-device tasks.',
    },
    over_file_size: {
        id: '57f43cb8a8',
        description: 'Error shown when one attached file is larger than an on-device task accepts, without naming the file',
        defaultMessage: 'One of these files is too large for on-device tasks.',
    },
});
export function getConnectionTypeFromUrl(url) {
    if (url.startsWith('/rest/connectors/wiley/')) {
        return 'WILEY';
    }
    if (url.includes('drive.google.com') ||
        url.includes('connectors/google-drive') ||
        url.includes('docs.google.com')) {
        return 'GOOGLE_DRIVE';
    }
    if (url.includes('onedrive.live.com') ||
        url.includes('1drv.ms') ||
        url.includes('connectors/onedrive')) {
        return 'ONEDRIVE';
    }
    if (url.includes('.sharepoint.com') || url.includes('connectors/sharepoint')) {
        return 'SHAREPOINT';
    }
    if (url.includes('dropbox.com/preview') || url.includes('connectors/dropbox')) {
        return 'DROPBOX';
    }
    if (url.includes('app.box.com/file') || url.includes('connectors/box')) {
        return 'BOX';
    }
    return undefined;
}
export function getConnectionTypeFromWebResult(webResult) {
    const metaData = webResult.meta_data;
    const fromMeta = metaData?.connection_type ??
        webResult.file_metadata?.connector_type;
    if (fromMeta) {
        return fromMeta;
    }
    if (!webResult.url) {
        return undefined;
    }
    return getConnectionTypeFromUrl(webResult.url);
}
export function isPatentResult(result) {
    return getIsPatentSource(result);
}
export function isConnectorFile(result) {
    const metaData = result?.meta_data;
    return (!!(metaData?.connection_type && metaData.connection_type !== 'LOCAL') ||
        (!!result.is_attachment && !!result.url && !!getConnectionTypeFromUrl(result.url)));
}
export function isDownloadable(result) {
    return !!result.is_attachment && !!result.url && isDownloadableUrl(result.url);
}
export function isLocalMCPResult(result) {
    if (!result) {
        return false;
    }
    const metaData = result.meta_data;
    return Object.values(LocalMCPServerName).includes(metaData?.mcp_server);
}
//# sourceMappingURL=attachmentsUtils.js.map